import L from 'leaflet';

/**
 * Leaflet's zoom animation outlives the map it belongs to.
 *
 * `Map._animateZoom` schedules `_onZoomTransitionEnd` on a 250ms timeout as a workaround for
 * browsers that never fire `transitionend`. `Map.remove()` deletes `_mapPane` but neither cancels
 * that timeout nor clears `_animatingZoom`, so a map that unmounts mid-zoom still runs the
 * callback. `_onZoomTransitionEnd` only null-checks `_mapPane` for the `removeClass` call and then
 * goes on to `_move()` -> `_getNewPixelOrigin()` -> `_getMapPanePos()` -> `DomUtil.getPosition()`,
 * which throws on `el._leaflet_pos`. The same `_move()` re-fires `zoom`/`move`/`moveend` at layers
 * that have already been detached, which is where `this._map.getZoom()` blows up.
 *
 * Mobile Safari hits this constantly because it drops `transitionend` when a container is removed
 * during the transition, so the 250ms fallback is the only path that ever runs.
 *
 * Bail out instead, and clear the flag so any later duplicate callback is a no-op too.
 */
const patchLeafletZoomAnimation = (): void => {
	const proto = L.Map.prototype as unknown as {
		_onZoomTransitionEnd?: () => void;
		__posturinnZoomEndGuarded?: boolean;
	};

	if (proto.__posturinnZoomEndGuarded) return;

	const original = proto._onZoomTransitionEnd;

	if (typeof original !== 'function') return;

	proto._onZoomTransitionEnd = function patchedOnZoomTransitionEnd(this: {
		_mapPane?: HTMLElement;
		_container?: HTMLElement;
		_animatingZoom?: boolean;
	}) {
		if (!this._mapPane || !this._container) {
			this._animatingZoom = false;
			return;
		}

		original.call(this);
	};

	proto.__posturinnZoomEndGuarded = true;
};

patchLeafletZoomAnimation();

export default patchLeafletZoomAnimation;
